import type {
  CreateIdentityResult,
  LoginIdentityResult,
  UnlockIdentityResult,
} from '../hooks/useIdentity.types';

/** Maps create-alias error codes to locale keys. */
export function createIdentityErrorI18nKey(
  errorCode?: CreateIdentityResult['errorCode'] | string,
): string {
  switch (errorCode) {
    case 'USERNAME_TAKEN':
      return 'identity.create.errorUsernameTaken';
    case 'MAX_ALIASES':
      return 'identity.create.errorMaxIdentities';
    case 'PASSPHRASE_EXISTS':
      return 'identity.create.errorPassphraseExists';
    case 'USERNAME_BLOCKED':
      return 'identity.create.errorUsernameBlocked';
    case 'USERNAME_NOT_GENERATED':
      return 'identity.create.errorUsernameNotGenerated';
    case 'DISPLAY_NAME_BLOCKED':
      return 'identity.create.errorDisplayNameBlocked';
    case 'E2E_INIT_FAILED':
      return 'identity.e2e.keyGenerationFailed';
    case 'PAYLOAD_TOO_LARGE':
      return 'identity.create.errorPayloadTooLarge';
    case 'GEOFENCE_BLOCKED':
      return 'compliance.geofence.description';
    case 'AGE_VERIFICATION_REQUIRED':
      return 'compliance.ageVerification.description';
    case 'AGE_VERIFICATION_FAILED':
      return 'compliance.ageVerification.failedMessage';
    case 'AGE_VERIFICATION_COOLDOWN':
      return 'compliance.ageVerification.cooldownMessage';
    case 'NO_SESSION':
      return 'identity.login.errorSession';
    default:
      return 'identity.create.errorValidation';
  }
}

/** Maps login error codes to locale keys. RATE_LIMITED needs a `seconds` interpolation. */
export function loginIdentityErrorI18nKey(
  errorCode?: LoginIdentityResult['errorCode'] | string,
): string {
  switch (errorCode) {
    case 'LOCKED_OUT':
      return 'identity.login.errorLocked';
    case 'RATE_LIMITED':
      return 'identity.login.errorRateLimited';
    case 'KEY_DERIVATION_FAILED':
    case 'KEY_GENERATION_FAILED':
    case 'E2E_SETUP_FAILED':
      return 'identity.e2e.keyGenerationFailed';
    case 'BUNDLE_DECRYPT_FAILED':
      return 'identity.e2e.bundleDecryptFailed';
    case 'DEVICE_REGISTRATION_FAILED':
      return 'identity.e2e.deviceRegistrationFailed';
    case 'GEOFENCE_BLOCKED':
      return 'compliance.geofence.description';
    case 'AGE_VERIFICATION_REQUIRED':
      return 'compliance.ageVerification.description';
    case 'AGE_VERIFICATION_FAILED':
      return 'compliance.ageVerification.failedMessage';
    case 'AGE_VERIFICATION_COOLDOWN':
      return 'compliance.ageVerification.cooldownMessage';
    case 'NO_SESSION':
      return 'identity.login.errorSession';
    default:
      return 'identity.login.errorInvalid';
  }
}

export function unlockIdentityErrorI18nKey(
  errorCode?: UnlockIdentityResult['errorCode'] | string,
): string {
  switch (errorCode) {
    case 'NO_SESSION':
      return 'identity.unlock.errorInvalid';
    case 'DEVICE_REGISTRATION_FAILED':
      return 'identity.e2e.deviceRegistrationFailed';
    case 'E2E_SETUP_FAILED':
      return 'identity.e2e.bundleUploadFailed';
    default:
      return 'identity.unlock.errorInvalid';
  }
}

export function changePassphraseErrorI18nKey(errorCode?: string): string {
  switch (errorCode) {
    case 'PASSPHRASE_EXISTS':
    case 'COLLISION':
      return 'identity.privacy.changePassword.errorPassphraseExists';
    case 'INVALID_PASSPHRASE':
      return 'identity.privacy.changePassword.errorInvalid';
    case 'BUNDLE_NOT_FOUND':
      return 'identity.privacy.changePassword.errorBundleNotFound';
    case 'VALIDATION_ERROR':
    case 'VALIDATION_FAILED':
      return 'identity.privacy.changePassword.errorMinLength';
    default:
      return 'identity.privacy.changePassword.errorFailed';
  }
}
